import React, { useState, useEffect, useRef } from "react";
import { BiRefresh } from "react-icons/bi";
import * as url from "../../../../utils/url";
import axios from "../../../../axios";
import ChatMessage from "./ChatMessage";
import { stripSmsBoilerplate } from "../../../../utils/stripSmsBoilerplate";

const parseFileNameFromContentDisposition = (contentDisposition) => {
  if (!contentDisposition || typeof contentDisposition !== "string") return "";

  try {
    const utf8Match = contentDisposition.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
    if (utf8Match && utf8Match[1]) {
      return decodeURIComponent(utf8Match[1].trim().replace(/^"|"$/g, ""));
    }

    const quotedMatch = contentDisposition.match(/filename\s*=\s*"([^"]+)"/i);
    if (quotedMatch && quotedMatch[1]) {
      return quotedMatch[1].trim();
    }

    const plainMatch = contentDisposition.match(/filename\s*=\s*([^;]+)/i);
    if (plainMatch && plainMatch[1]) {
      return plainMatch[1].trim().replace(/^"|"$/g, "");
    }
  } catch (err) {
    return "";
  }

  return "";
};

const getFileNameFromUrl = (fileUrl) => {
  if (!fileUrl || typeof fileUrl !== "string") return "";

  try {
    const baseUrl = typeof window !== "undefined" && window.location ? window.location.origin : "http://localhost";
    const parsedUrl = new URL(fileUrl, baseUrl);

    // S3 signed URLs may carry the real filename in response-content-disposition.
    // Prefer that value because the path/key can be generic or encoded.
    const disposition =
      parsedUrl.searchParams.get("response-content-disposition") ||
      parsedUrl.searchParams.get("ResponseContentDisposition") ||
      "";
    const dispositionFileName = parseFileNameFromContentDisposition(disposition);
    if (dispositionFileName) return dispositionFileName;

    const cleanPath = parsedUrl.pathname || "";
    const parts = cleanPath.split("/");
    return decodeURIComponent(parts[parts.length - 1] || "");
  } catch (err) {
    try {
      const cleanUrl = fileUrl.split("?")[0].split("#")[0];
      const parts = cleanUrl.split("/");
      return decodeURIComponent(parts[parts.length - 1] || "");
    } catch (fallbackErr) {
      return "";
    }
  }
};

const getFileExtension = (fileNameOrUrl) => {
  const rawValue = fileNameOrUrl ? String(fileNameOrUrl) : "";
  const value =
    rawValue.indexOf("?") !== -1 || rawValue.indexOf("/") !== -1
      ? getFileNameFromUrl(rawValue) || rawValue
      : rawValue;
  const cleanValue = value.split("?")[0].split("#")[0];
  const parts = cleanValue.split(".");
  if (parts.length < 2) return "";
  return parts[parts.length - 1].toLowerCase();
};

const formatFileSize = (size) => {
  const bytes = Number(size);
  if (!bytes || Number.isNaN(bytes)) return "";
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1024 * 1024) return Math.round(bytes / 1024) + " KB";
  return (bytes / (1024 * 1024)).toFixed(1) + " MB";
};

const AUDIO_BROWSER_EXTENSIONS = ["mp3", "mpeg", "wav", "m4a", "aac", "ogg", "oga", "opus", "webm"];
const VIDEO_BROWSER_EXTENSIONS = ["mp4", "m4v", "webm", "ogv", "ogg", "mov"];

const getBestPlayableUrlCandidate = (rawAttachment) => {
  if (!rawAttachment || typeof rawAttachment !== "object") return "";
  return (
    rawAttachment.browserPlayableUrl ||
    rawAttachment.playableUrl ||
    rawAttachment.transcodedUrl ||
    rawAttachment.convertedUrl ||
    rawAttachment.previewUrl ||
    rawAttachment.audioPreviewUrl ||
    rawAttachment.videoPreviewUrl ||
    rawAttachment.streamUrl ||
    rawAttachment.streamingUrl ||
    rawAttachment.mp3Url ||
    rawAttachment.m4aUrl ||
    rawAttachment.mp4Url ||
    ""
  );
};

const isBrowserPlayableAudio = (attachment) => {
  if (!attachment) return false;
  const mimeType = attachment.mimeType ? String(attachment.mimeType).toLowerCase() : "";
  const extension =
    getFileExtension(attachment.fileName) ||
    getFileExtension(attachment.filename) ||
    getFileExtension(attachment.name) ||
    getFileExtension(attachment.originalName) ||
    getFileExtension(attachment.originalFilename) ||
    getFileExtension(attachment.url) ||
    getFileExtension(attachment.fileUrl) ||
    getFileExtension(attachment.mediaUrl) ||
    getFileExtension(attachment.attachmentUrl) ||
    getFileExtension(attachment.signedUrl) ||
    getFileExtension(attachment.playableUrl) ||
    getFileExtension(attachment.browserPlayableUrl);

  // AMR/AMR-WB commonly arrives from MMS, but native browser audio controls do
  // not reliably play it. It needs a converted MP3/M4A/WAV preview URL.
  if (extension === "amr" || extension === "awb" || mimeType.indexOf("amr") !== -1) {
    return false;
  }

  return (
    AUDIO_BROWSER_EXTENSIONS.indexOf(extension) !== -1 ||
    mimeType.indexOf("audio/mpeg") === 0 ||
    mimeType.indexOf("audio/mp3") === 0 ||
    mimeType.indexOf("audio/wav") === 0 ||
    mimeType.indexOf("audio/x-wav") === 0 ||
    mimeType.indexOf("audio/mp4") === 0 ||
    mimeType.indexOf("audio/aac") === 0 ||
    mimeType.indexOf("audio/ogg") === 0 ||
    mimeType.indexOf("audio/webm") === 0
  );
};

const isBrowserPlayableVideo = (attachment) => {
  if (!attachment) return false;
  const mimeType = attachment.mimeType ? String(attachment.mimeType).toLowerCase() : "";
  const extension =
    getFileExtension(attachment.fileName) ||
    getFileExtension(attachment.filename) ||
    getFileExtension(attachment.name) ||
    getFileExtension(attachment.originalName) ||
    getFileExtension(attachment.originalFilename) ||
    getFileExtension(attachment.url) ||
    getFileExtension(attachment.fileUrl) ||
    getFileExtension(attachment.mediaUrl) ||
    getFileExtension(attachment.attachmentUrl) ||
    getFileExtension(attachment.signedUrl) ||
    getFileExtension(attachment.playableUrl) ||
    getFileExtension(attachment.browserPlayableUrl);

  return (
    VIDEO_BROWSER_EXTENSIONS.indexOf(extension) !== -1 ||
    mimeType.indexOf("video/mp4") === 0 ||
    mimeType.indexOf("video/webm") === 0 ||
    mimeType.indexOf("video/ogg") === 0 ||
    mimeType.indexOf("video/quicktime") === 0
  );
};

const inferAttachmentKind = (attachment) => {
  if (!attachment) return "file";
  const mimeType = attachment.mimeType ? String(attachment.mimeType).toLowerCase() : "";
  const extension =
    getFileExtension(attachment.fileName) ||
    getFileExtension(attachment.filename) ||
    getFileExtension(attachment.name) ||
    getFileExtension(attachment.originalName) ||
    getFileExtension(attachment.originalFilename) ||
    getFileExtension(attachment.url) ||
    getFileExtension(attachment.fileUrl) ||
    getFileExtension(attachment.mediaUrl) ||
    getFileExtension(attachment.attachmentUrl) ||
    getFileExtension(attachment.signedUrl) ||
    getFileExtension(attachment.playableUrl) ||
    getFileExtension(attachment.browserPlayableUrl);

  if (mimeType.indexOf("image/") === 0 || ["jpg", "jpeg", "png", "gif", "webp", "heic", "heif", "bmp"].indexOf(extension) !== -1) {
    return "image";
  }

  if (mimeType.indexOf("pdf") !== -1 || extension === "pdf") {
    return "pdf";
  }

  if (mimeType.indexOf("audio/") === 0 || ["mp3", "mpeg", "wav", "m4a", "aac", "ogg", "oga", "opus", "amr", "awb", "flac", "webm"].indexOf(extension) !== -1) {
    return "audio";
  }

  if (mimeType.indexOf("video/") === 0 || ["mp4", "mov", "m4v", "webm", "avi", "wmv", "mkv", "3gp", "3g2", "ogv"].indexOf(extension) !== -1) {
    return "video";
  }

  return "file";
};

const normalizeAttachment = (rawAttachment) => {
  if (!rawAttachment) return null;

  if (typeof rawAttachment === "string") {
    const urlValue = rawAttachment;
    const fileNameValue = getFileNameFromUrl(urlValue);
    const normalizedStringAttachment = {
      url: urlValue,
      fileName: fileNameValue,
      mimeType: "",
      fileSize: null,
      playableUrl: "",
    };
    const kind = inferAttachmentKind(normalizedStringAttachment);
    return {
      ...normalizedStringAttachment,
      kind,
      playableUrl:
        kind === "audio" && isBrowserPlayableAudio(normalizedStringAttachment)
          ? urlValue
          : kind === "video" && isBrowserPlayableVideo(normalizedStringAttachment)
            ? urlValue
            : "",
    };
  }

  if (typeof rawAttachment !== "object") return null;

  const urlValue =
    rawAttachment.url ||
    rawAttachment.fileUrl ||
    rawAttachment.mediaUrl ||
    rawAttachment.attachmentUrl ||
    rawAttachment.signedUrl ||
    rawAttachment.secureUrl ||
    rawAttachment.location ||
    rawAttachment.href ||
    "";

  const playableUrlValue = getBestPlayableUrlCandidate(rawAttachment);

  const fileNameValue =
    rawAttachment.fileName ||
    rawAttachment.filename ||
    rawAttachment.name ||
    rawAttachment.originalName ||
    rawAttachment.originalFilename ||
    rawAttachment.key ||
    getFileNameFromUrl(urlValue) ||
    getFileNameFromUrl(playableUrlValue) ||
    "Attachment";

  const normalizedObjectAttachment = {
    url: urlValue,
    fileName: fileNameValue,
    mimeType:
      rawAttachment.mimeType ||
      rawAttachment.contentType ||
      rawAttachment.mediaContentType ||
      rawAttachment.type ||
      "",
    fileSize:
      rawAttachment.fileSize ||
      rawAttachment.size ||
      rawAttachment.bytes ||
      rawAttachment.contentLength ||
      null,
    playableUrl: playableUrlValue,
  };

  const kind = inferAttachmentKind(normalizedObjectAttachment);
  const playableCandidate = normalizedObjectAttachment.playableUrl;

  return {
    ...normalizedObjectAttachment,
    kind,
    playableUrl:
      playableCandidate ||
      (kind === "audio" && isBrowserPlayableAudio(normalizedObjectAttachment)
        ? urlValue
        : kind === "video" && isBrowserPlayableVideo(normalizedObjectAttachment)
          ? urlValue
          : ""),
    needsConversion:
      (kind === "audio" && !playableCandidate && !isBrowserPlayableAudio(normalizedObjectAttachment)) ||
      (kind === "video" && !playableCandidate && !isBrowserPlayableVideo(normalizedObjectAttachment)),
  };
};

const appendAttachmentCandidates = (candidates, value) => {
  if (!value) return;
  if (Array.isArray(value)) {
    value.forEach((item) => appendAttachmentCandidates(candidates, item));
    return;
  }
  candidates.push(value);
};

const getFirstMessagingAttachment = (source) => {
  if (!source) return null;

  const messagingResponse = source.messagingResponse || {};
  const candidates = [];

  appendAttachmentCandidates(candidates, source.attachment);
  appendAttachmentCandidates(candidates, source.attachments);
  appendAttachmentCandidates(candidates, messagingResponse.attachments);
  appendAttachmentCandidates(candidates, messagingResponse.attachment);
  appendAttachmentCandidates(candidates, messagingResponse.media);
  appendAttachmentCandidates(candidates, messagingResponse.mediaFile);
  appendAttachmentCandidates(candidates, messagingResponse.mediaFiles);
  appendAttachmentCandidates(candidates, messagingResponse.files);
  appendAttachmentCandidates(candidates, messagingResponse.file);
  appendAttachmentCandidates(candidates, messagingResponse.mediaUrl);
  appendAttachmentCandidates(candidates, messagingResponse.mediaUrls);
  appendAttachmentCandidates(candidates, messagingResponse.fileUrl);
  appendAttachmentCandidates(candidates, messagingResponse.attachmentUrl);
  appendAttachmentCandidates(candidates, messagingResponse.signedUrl);
  appendAttachmentCandidates(candidates, source.media);
  appendAttachmentCandidates(candidates, source.mediaFile);
  appendAttachmentCandidates(candidates, source.mediaFiles);
  appendAttachmentCandidates(candidates, source.files);
  appendAttachmentCandidates(candidates, source.file);
  appendAttachmentCandidates(candidates, source.mediaUrl);
  appendAttachmentCandidates(candidates, source.mediaUrls);
  appendAttachmentCandidates(candidates, source.fileUrl);
  appendAttachmentCandidates(candidates, source.attachmentUrl);
  appendAttachmentCandidates(candidates, source.signedUrl);

  for (let i = 0; i < candidates.length; i++) {
    const normalized = normalizeAttachment(candidates[i]);
    if (normalized && (normalized.url || normalized.fileName)) {
      return normalized;
    }
  }

  return null;
};

const getOriginalMessageText = function(t) {
  if (!t) return "";
  if (t.response && typeof t.response === "string") return t.response;
  if (t.messageText && typeof t.messageText === "string") return t.messageText;
  if (t.text && typeof t.text === "string") return t.text;
  if (t.freeText && typeof t.freeText === "string") return t.freeText;
  if (t.messagingResponse && typeof t.messagingResponse.text === "string") {
    return t.messagingResponse.text;
  }
  if (t.messagingResponse && typeof t.messagingResponse.body === "string") {
    return t.messagingResponse.body;
  }
  return "";
};

const buildOriginalHistoryItem = function(trivia) {
  const originalText = getOriginalMessageText(trivia);
  const originalAttachment = trivia ? trivia.attachment || getFirstMessagingAttachment(trivia) : null;

  if (!originalText && !originalAttachment) return null;

  return {
    id: "original",
    senderType: "USER",
    senderId: trivia && trivia.user && trivia.user._id ? trivia.user._id : null,
    senderFirstName: trivia && trivia.user ? trivia.user.firstName : null,
    senderLastName: trivia && trivia.user ? trivia.user.lastName : null,
    text: originalText,
    createdAt: trivia ? trivia.sentDate || trivia.date || trivia.createdAt || null : null,
    attachment: originalAttachment,
  };
};

const getMessageText = function(message, fallbackText) {
  if (!message) return fallbackText || "";
  return stripSmsBoilerplate(
    message.body ||
    message.text ||
    message.message ||
    message.replyText ||
    message.content ||
    fallbackText ||
    ""
  );
};

const mapBackendMessage = function(message, fallbackText, currentUserId, currentUserFirstName, currentUserLastName) {
  if (!message) return null;

  const id = message._id || message.id || message.messageId || "message-" + Date.now();

  return {
    id: id,
    senderType: message.senderType || "ADMIN",
    senderId:
      message.senderId !== undefined && message.senderId !== null
        ? message.senderId
        : currentUserId,
    senderFirstName:
      message.senderFirstName !== undefined && message.senderFirstName !== null
        ? message.senderFirstName
        : currentUserFirstName,
    senderLastName:
      message.senderLastName !== undefined && message.senderLastName !== null
        ? message.senderLastName
        : currentUserLastName,
    text: getMessageText(message, fallbackText),
    createdAt: message.createdAt || message.date || message.sentAt || new Date().toISOString(),
    attachment: getFirstMessagingAttachment(message),
  };
};


const extractMessagesFromConversationResponse = function(data) {
  if (!data) return [];

  if (Array.isArray(data.messages)) return data.messages;
  if (data.data && Array.isArray(data.data.messages)) return data.data.messages;
  if (data.conversation && Array.isArray(data.conversation.messages)) return data.conversation.messages;
  if (data.data && data.data.conversation && Array.isArray(data.data.conversation.messages)) {
    return data.data.conversation.messages;
  }
  if (data.result && Array.isArray(data.result.messages)) return data.result.messages;
  if (data.thread && Array.isArray(data.thread.messages)) return data.thread.messages;
  if (data.conversationMessages && Array.isArray(data.conversationMessages)) {
    return data.conversationMessages;
  }
  if (data.data && Array.isArray(data.data)) return data.data;

  return [];
};

const extractSavedMessageFromResponse = function(data) {
  if (!data) return null;

  if (data.message) return data.message;
  if (data.reply) return data.reply;
  if (data.conversationMessage) return data.conversationMessage;
  if (data.savedMessage) return data.savedMessage;
  if (data.data && data.data.message) return data.data.message;
  if (data.data && data.data.reply) return data.data.reply;

  if (Array.isArray(data.messages) && data.messages.length > 0) {
    return data.messages[data.messages.length - 1];
  }

  return null;
};

const mergeMessages = function(messages) {
  const seen = {};
  const merged = [];

  messages.forEach(function(message) {
    if (!message) return;

    const id = message.id ? String(message.id) : "";
    const text = message.text || "";
    const createdAt = message.createdAt || "";
    const key = id || message.senderType + "|" + text + "|" + createdAt;

    if (seen[key]) return;
    seen[key] = true;
    merged.push(message);
  });

  return merged;
};

export default function TriviaSMSResponses({ trivia, user, adminAId }) {
  const currentUserId =
    user && user.profile && user.profile._id ? user.profile._id : null;
  const currentUserFirstName =
    user && user.profile && user.profile.firstName
      ? user.profile.firstName
      : "";
  const currentUserLastName =
    user && user.profile && user.profile.lastName
      ? user.profile.lastName
      : "";
  const chatRef = useRef();
  const [isOpen, setIsOpen] = useState(!!(trivia && trivia.comingFromNeedsAttention));
  const [message, setMessage] = useState("");
  const [history, setHistory] = useState([]); // [{ id, senderType, text, createdAt, attachment }]
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [hasFetched, setHasFetched] = useState(false); // avoid refetching every toggle

  const triviaResultId = trivia && trivia._id ? trivia._id : null;

  const scrollToBottom = function() {
    setTimeout(function() {
      if (chatRef.current) {
        chatRef.current.scrollTop = chatRef.current.scrollHeight;
      }
    }, 0);
  };

  // When the modal opens or switches rows, seed history with the user's original message.
  useEffect(
    function() {
      const originalItem = buildOriginalHistoryItem(trivia);
      setHistory(originalItem ? [originalItem] : []);
      setHasFetched(false);
    },
    [triviaResultId]
  );

  useEffect(
    function() {
      if (trivia && trivia.comingFromNeedsAttention) {
        fetchConversation();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    []
  );

  // Guard: only show for messaging trivvys
  if (!trivia || !trivia.isMessaging) return null;

  const handleToggle = function() {
    const next = !isOpen;
    setIsOpen(next);

    // Lazy-load conversation from backend the first time the panel is opened
    if (next && !hasFetched && !loading) {
      fetchConversation();
    }
  };

  const fetchConversation = async function() {
    if (!triviaResultId) return;
    setLoading(true);
    setError(null);

    try {
      const res = await axios.get(url.getTriviaConversation(triviaResultId));
      setHasFetched(true);

      const data = res && res.data ? res.data : null;
      const rawMessages = extractMessagesFromConversationResponse(data);

      // Important: only overwrite/merge history when the backend actually
      // returns conversation messages. If the response shape is unexpected or
      // the API returns an empty/missing messages array, keep the current local
      // state instead of wiping previously visible replies.
      if (rawMessages && rawMessages.length > 0) {
        const backendMessages = rawMessages
          .map(function(m) {
            return mapBackendMessage(
              m,
              "",
              currentUserId,
              currentUserFirstName,
              currentUserLastName
            );
          })
          .filter(Boolean);

        const originalItem = buildOriginalHistoryItem(trivia);
        const baseMessages = originalItem ? [originalItem] : [];

        setHistory(function(prev) {
          // Preserve local replies that were added optimistically and have not
          // been returned by the backend yet.
          const localMessages = prev.filter(function(item) {
            return item && item.id && String(item.id).indexOf("local-") === 0;
          });

          return mergeMessages(baseMessages.concat(backendMessages).concat(localMessages));
        });
      } else {
        setHistory(function(prev) {
          if (prev && prev.length > 0) return prev;
          const originalItem = buildOriginalHistoryItem(trivia);
          return originalItem ? [originalItem] : [];
        });
      }

      scrollToBottom();
    } catch (err) {
      console.error("Error fetching conversation", err);

      // If backend returns 404 for "no conversation yet", don't show an error.
      if (err.response && err.response.status === 404) {
        setHasFetched(true);
        // no history change; keep the seeded original message and any local replies
      } else {
        // Real failure (500, network, etc)
        setError("Failed to load conversation.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async function() {
    const trimmed = (message || "").trim();
    if (!trimmed || sending || !triviaResultId) return;

    setSending(true);
    setError(null);

    // Optimistic UI: append the ADMIN message directly to local state.
    // This is what makes the reply appear immediately in the modal without
    // needing a page refresh.
    const tempId = "local-" + Date.now();
    const optimisticMessage = {
      id: tempId,
      senderType: "ADMIN",
      senderId: currentUserId,
      senderFirstName: currentUserFirstName,
      senderLastName: currentUserLastName,
      text: trimmed,
      body: trimmed,
      createdAt: new Date().toISOString(),
      pending: true,
    };

    setHistory(function(prev) {
      return mergeMessages((prev || []).concat(optimisticMessage));
    });
    setMessage("");
    scrollToBottom();

    try {
      const res = await axios.post(url.replyToTriviaResult, {
        triviaResultId: triviaResultId,
        replyText: trimmed,
        channel: "SMS",
      });

      const data = res && res.data ? res.data : null;
      const savedMessage = extractSavedMessageFromResponse(data);
      const mappedSavedMessage = savedMessage
        ? mapBackendMessage(
            savedMessage,
            trimmed,
            currentUserId,
            currentUserFirstName,
            currentUserLastName
          )
        : null;

      // If the backend returns the saved message, replace the temp message.
      // If it only returns success/no body, keep the local message and simply
      // remove the pending flag. Do not clear or refetch the whole history here,
      // because that can temporarily wipe existing replies if the response does
      // not include the full conversation.
      setHistory(function(prev) {
        return (prev || []).map(function(item) {
          if (item.id !== tempId) return item;

          if (mappedSavedMessage) {
            return {
              ...mappedSavedMessage,
              pending: false,
              failed: false,
            };
          }

          return {
            ...item,
            pending: false,
            failed: false,
          };
        });
      });

      scrollToBottom();
    } catch (err) {
      console.error("Error sending reply", err);

      // Keep the local message visible, but mark it failed. This avoids the
      // confusing behavior where the message disappears from the modal.
      setHistory(function(prev) {
        return (prev || []).map(function(item) {
          if (item.id !== tempId) return item;
          return {
            ...item,
            pending: false,
            failed: true,
          };
        });
      });
      setError("Failed to send message. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const renderSenderLabel = function(item) {
    const userName =
      trivia.user && !trivia.isAnonymous
        ? `${trivia.user.firstName} ${trivia.user.lastName}`
        : "Anonymous user";

    // Mine (viewer) → "You"
    if (
      currentUserId &&
      item.senderId &&
      String(item.senderId) === String(currentUserId)
    ) {
      return "You";
    }

    // Original user message seeded without senderId, or a USER-type message →
    // show the Trivvy recipient's name (respecting anonymity)
    if (!item.senderId || item.senderType === "USER") {
      return userName;
    }

    // Admin A (original Trivvy creator) → anonymized as "Admin"
    if (adminAId && String(item.senderId) === String(adminAId)) {
      return "Admin";
    }

    // Any other admin (Admin B) → show their real name
    const first = item.senderFirstName || "";
    const last = item.senderLastName || "";
    const full = (first + " " + last).trim();
    if (full) return full;

    if (item.senderType === "ADMIN") return "Admin";
    return "System";
  };

  return (
    <div>
      <div className="d-flex align-items-center bwai-chat-actions">
        <button
          type="button"
          className="btn bwai-chat-toggle"
          onClick={handleToggle}
        >
          {isOpen ? "Hide" : "Respond"}
        </button>
        {isOpen && (
          <span
            className="bwai-chat-refresh"
            onClick={function() {
              // Do not clear local state before refreshing. If the fetch returns
              // an empty or unexpected payload, clearing here would make existing
              // replies disappear until a page reload.
              setHasFetched(false);
              fetchConversation();
            }}
            title="Refresh chat"
          >
            <BiRefresh size={16} /> Refresh chat
          </span>
        )}
      </div>

      {isOpen && (
        <div className="bwai-chat-section">
          <div className="bwai-response-eyebrow">
            Message History
          </div>
          {/* History / loading / errors */}
          {loading ? (
            <div className="text-muted mb-2">Loading conversation…</div>
          ) : (
            <div
              ref={chatRef}
              className="chat-history bwai-chat-history mb-2"
            >
              {history.length === 0 ? (
                <div className="text-muted text-center">No messages yet.</div>
              ) : (
                history.map(function(item, idx) {
                  return (
                    <ChatMessage
                      key={item.id || idx}
                      item={item}
                      renderSenderLabel={renderSenderLabel}
                      currentUserId={currentUserId}
                    />
                  );
                })
              )}
            </div>
          )}

          {error && <div className="alert alert-danger py-1 mb-2">{error}</div>}

          {/* Textarea + send */}
          <textarea
            className="form-control bwai-chat-input"
            rows={3}
            value={message}
            onChange={function(e) {
              setMessage(e.target.value);
            }}
            placeholder="Type your response…"
          />
          <div className="bwai-chat-send-row">
            <button
              type="button"
              className="btn bwai-chat-send"
              onClick={handleSend}
              disabled={sending}
            >
              {sending ? "Sending…" : "Reply"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
